import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { C, font } from "../theme";

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  id?: string;
  value: string;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  style?: CSSProperties;
  ariaLabel?: string;
}

/**
 * Select do Apogeu: gatilho estilizado com a paleta do design system + dropdown
 * próprio. O dropdown é renderizado via portal fixo no `document.body` para que,
 * dentro de modais com `overflow`, ele apareça FORA do scroll e não gere rolagem.
 */
export function Select({
  id,
  value,
  options,
  placeholder = "Selecione...",
  disabled,
  onChange,
  style,
  ariaLabel,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => setTriggerRect(triggerRef.current?.getBoundingClientRect() ?? null);
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = options.find(o => o.value === value);
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
  const minWidth = Math.min(Math.max(120, triggerRect?.width ?? 0), Math.max(0, viewportWidth - 16));
  const fitsBelow = triggerRect ? triggerRect.bottom + 260 <= viewportHeight : true;
  const left = triggerRect ? Math.max(8, Math.min(triggerRect.left, viewportWidth - minWidth - 8)) : 8;

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 0, ...style }}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          width: "100%",
          fontFamily: font,
          fontSize: 13,
          color: selected ? C.ink : C.light,
          border: `1px solid ${C.faint}`,
          borderRadius: 10,
          padding: "10px 12px",
          background: C.white,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          textAlign: "left",
          minWidth: 0,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={15}
          style={{ flexShrink: 0, color: C.gray, transform: open ? "rotate(180deg)" : undefined, transition: "transform 0.15s" }}
        />
      </button>

      {open && triggerRect && createPortal(
        <div
          ref={panelRef}
          role="listbox"
          style={{
            position: "fixed",
            // The menu is portaled to body, so it must sit above the modal overlay.
            zIndex: 110,
            left,
            minWidth,
            width: minWidth,
            maxWidth: "calc(100vw - 16px)",
            boxSizing: "border-box",
            background: C.white,
            border: `1px solid ${C.faint}`,
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(17,17,17,0.12)",
            padding: 4,
            maxHeight: fitsBelow ? Math.min(240, viewportHeight - triggerRect.bottom - 8) : Math.min(240, triggerRect.top - 8),
            overflowY: "auto",
            top: fitsBelow ? triggerRect.bottom + 4 : undefined,
            bottom: fitsBelow ? undefined : viewportHeight - triggerRect.top + 4,
          }}
        >
          {options.map(o => {
            const isSelected = o.value === value;
            const isHovered = hovered === o.value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                onMouseEnter={() => setHovered(o.value)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  fontFamily: font,
                  fontSize: 13,
                  color: C.ink,
                  background: isSelected || isHovered ? C.limeSoft : "transparent",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 10px",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ flex: 1 }}>{o.label}</span>
                {isSelected && <Check size={14} style={{ color: C.ink }} />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
